import React, { useState, useEffect, useRef } from 'react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

export default function LiveTracking() {
  const [positions, setPositions] = useState([]);
  const [shops, setShops] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tick, setTick] = useState(0);
  const [mapLoaded, setMapLoaded] = useState(false);

  const mapContainerRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markersRef = useRef({});
  const shopMarkersRef = useRef({});

  // 1. Dynamic Script & CSS Loader for Leaflet
  useEffect(() => {
    // Load CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    link.id = 'leaflet-css';
    document.head.appendChild(link);

    // Load JS
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.async = true;
    script.id = 'leaflet-js';
    script.onload = () => {
      setMapLoaded(true);
    };
    document.head.appendChild(script);

    return () => {
      const existingLink = document.getElementById('leaflet-css');
      const existingScript = document.getElementById('leaflet-js');
      if (existingLink) document.head.removeChild(existingLink);
      if (existingScript) document.head.removeChild(existingScript);
    };
  }, []);

  // 2. Fetch Delivery Boys and Shops from Firestore (real-time)
  useEffect(() => {
    const q = query(collection(db, 'users'));
    const unsub = onSnapshot(q, snap => {
      const allUsers = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filter riders
      const riders = allUsers
        .filter(d => d.role === 'deliveryboy' || d.role === 'Delivery Boy')
        .map(d => {
          return {
            id: d.id,
            name: d.name || 'Delivery Boy',
            status: d.status || 'Offline',
            phone: d.phone || '',
            latitude: d.currentLocation?.latitude || null,
            longitude: d.currentLocation?.longitude || null,
            color: d.status === 'Active' ? '#22C55E' : d.status === 'Paused' ? '#F59E0B' : '#64748B'
          };
        });
      setPositions(riders);

      // Filter shops
      const shopkeepers = allUsers
        .filter(d => d.role === 'shopkeeper')
        .map(d => {
          return {
            id: d.id,
            name: d.shopName || d.name || 'Shop',
            category: d.category || 'General',
            latitude: d.coords?.latitude || null,
            longitude: d.coords?.longitude || null,
            isOpen: d.isShopOpen !== undefined ? d.isShopOpen : true,
            status: d.status || 'Active'
          };
        });
      setShops(shopkeepers);

      setTick(t => t + 1);
    }, err => console.error("Firestore onSnapshot error:", err));

    return () => unsub();
  }, []);

  // 3. Initialize Leaflet Map
  useEffect(() => {
    if (!mapLoaded || !mapContainerRef.current) return;

    const L = window.L;
    // Center of Sangola coordinates
    const map = L.map(mapContainerRef.current).setView([17.4332, 75.1979], 14);
    leafletMapRef.current = map;

    // Use OpenStreetMap Tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, [mapLoaded]);

  // 4. Update Delivery Boy Markers
  useEffect(() => {
    if (!mapLoaded || !leafletMapRef.current) return;

    const L = window.L;
    const map = leafletMapRef.current;
    const currentMarkers = markersRef.current;
    const activeIds = new Set();

    positions.forEach(boy => {
      if (boy.latitude && boy.longitude) {
        activeIds.add(boy.id);
        const latlng = [boy.latitude, boy.longitude];

        const popupContent = `
          <div style="font-family: inherit; font-size: 12px; gap: 4px; display: flex; flex-direction: column;">
            <strong style="font-size: 13px;">🛵 ${boy.name}</strong>
            <div>Status: <span style="color: ${boy.color}; font-weight: 700;">${boy.status}</span></div>
            ${boy.phone ? `<div>Phone: <strong>${boy.phone}</strong></div>` : ''}
          </div>
        `;

        if (currentMarkers[boy.id]) {
          currentMarkers[boy.id].setLatLng(latlng);
          currentMarkers[boy.id].getPopup().setContent(popupContent);
        } else {
          const iconHtml = `
            <div style="
              width: 36px; height: 36px; border-radius: 50%;
              background: ${boy.color}; border: 3px solid white;
              display: flex; align-items: center; justify-content: center;
              font-size: 16px; box-shadow: 0 0 12px rgba(0,0,0,0.4);
              position: relative;
            ">
              🛵
            </div>
          `;
          
          const customIcon = L.divIcon({
            html: iconHtml,
            className: 'custom-rider-icon',
            iconSize: [36, 36],
            iconAnchor: [18, 18]
          });

          const marker = L.marker(latlng, { icon: customIcon })
            .addTo(map)
            .bindPopup(popupContent);

          currentMarkers[boy.id] = marker;
        }
      }
    });

    Object.keys(currentMarkers).forEach(id => {
      if (!activeIds.has(id)) {
        currentMarkers[id].remove();
        delete currentMarkers[id];
      }
    });
  }, [positions, mapLoaded]);

  // 5. Update Shop Markers dynamically
  useEffect(() => {
    if (!mapLoaded || !leafletMapRef.current) return;

    const L = window.L;
    const map = leafletMapRef.current;
    const currentShopMarkers = shopMarkersRef.current;
    const activeShopIds = new Set();

    const getCategoryColor = (cat) => {
      if (cat === 'Grocery') return '#F59E0B'; // Amber
      if (cat === 'Food') return '#EF4444'; // Red
      if (cat === 'Clothes') return '#3B82F6'; // Blue
      return '#8B5CF6'; // Purple fallback
    };

    shops.forEach(shop => {
      if (shop.latitude && shop.longitude) {
        activeShopIds.add(shop.id);
        const latlng = [shop.latitude, shop.longitude];
        const shopColor = getCategoryColor(shop.category);

        const popupContent = `
          <div style="font-family: inherit; font-size: 12px; gap: 4px; display: flex; flex-direction: column;">
            <strong style="font-size: 13px;">🏪 ${shop.name}</strong>
            <div>Category: <strong>${shop.category}</strong></div>
            <div>Status: <span style="color: ${shop.isOpen ? '#22C55E' : '#EF4444'}; font-weight: 700;">${shop.isOpen ? 'Open ✅' : 'Closed ❌'}</span></div>
          </div>
        `;

        if (currentShopMarkers[shop.id]) {
          currentShopMarkers[shop.id].setLatLng(latlng);
          currentShopMarkers[shop.id].getPopup().setContent(popupContent);
        } else {
          const shopIconHtml = `
            <div style="
              width: 32px; height: 32px; border-radius: 6px;
              background: ${shopColor}; display: flex;
              align-items: center; justify-content: center;
              font-size: 16px; border: 2px solid white;
              box-shadow: 0 0 10px rgba(0,0,0,0.3);
            ">
              🏪
            </div>
          `;
          const shopIcon = L.divIcon({
            html: shopIconHtml,
            className: 'custom-shop-icon',
            iconSize: [32, 32],
            iconAnchor: [16, 16]
          });

          const marker = L.marker(latlng, { icon: shopIcon })
            .addTo(map)
            .bindPopup(popupContent);

          currentShopMarkers[shop.id] = marker;
        }
      }
    });

    Object.keys(currentShopMarkers).forEach(id => {
      if (!activeShopIds.has(id)) {
        currentShopMarkers[id].remove();
        delete currentShopMarkers[id];
      }
    });
  }, [shops, mapLoaded]);

  // Centering on a selected rider
  const handleSelectBoy = (boy) => {
    setSelected(selected?.id === boy.id ? null : boy);
    if (leafletMapRef.current && boy.latitude && boy.longitude) {
      leafletMapRef.current.setView([boy.latitude, boy.longitude], 16);
      if (markersRef.current[boy.id]) {
        markersRef.current[boy.id].openPopup();
      }
    }
  };

  return (
    <div>
      {/* Status metrics bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {[
          { l: 'Active Deliveries', v: positions.filter(d => d.status === 'Active').length, c: '#22C55E', icon: '🛵' },
          { l: 'Paused Riders', v: positions.filter(d => d.status === 'Paused').length, c: '#F59E0B', icon: '⏸️' },
          { l: 'Last Update', v: `${tick}s ago`, c: 'var(--success)', icon: '🔄' },
          { l: 'Coverage Area', v: 'Sangola HQ', c: 'var(--info)', icon: '📡' },
        ].map((s, i) => (
          <div key={i} style={{
            flex: 1, padding: '14px 16px',
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 10, textAlign: 'center'
          }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: s.c }}>{s.v}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.l}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 16 }}>
        {/* Map Card */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div className="card-title">📍 Live Map – Sangola</div>
              <div className="card-subtitle">Real-time interactive Leaflet tracking</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <span className="badge success" style={{ animation: 'pulse 2s infinite' }}>● LIVE</span>
            </div>
          </div>

          {/* Leaflet Map Div */}
          <div 
            ref={mapContainerRef} 
            style={{
              height: 450,
              width: '100%',
              backgroundColor: '#1e293b',
              position: 'relative',
              zIndex: 1
            }}
          />
        </div>

        {/* Sidebar Rider List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="card-title" style={{ marginBottom: 4, padding: '0 4px' }}>Active Riders ({positions.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 420, overflowY: 'auto' }}>
            {positions.map(d => (
              <div key={d.id}
                className="card"
                style={{
                  padding: '14px 16px', cursor: 'pointer',
                  borderColor: selected?.id === d.id ? d.color : 'var(--border)',
                  backgroundColor: selected?.id === d.id ? 'rgba(255,255,255,0.02)' : 'var(--bg-card)'
                }}
                onClick={() => handleSelectBoy(d)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: '50%',
                      background: d.color, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 13, flexShrink: 0
                    }}>🛵</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{d.name}</div>
                      {d.phone && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>📞 {d.phone}</div>}
                    </div>
                  </div>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', background: d.color,
                    display: 'inline-block', marginTop: 6,
                    animation: d.status === 'Active' ? 'pulse 1.5s infinite' : 'none'
                  }} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  Status: <strong style={{ color: d.color }}>{d.status}</strong>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                  {d.latitude ? '📍 GPS Tracked' : '🚫 No GPS Signal'}
                </div>
              </div>
            ))}
          </div>

          <div style={{ padding: '12px 16px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.8 }}>
            ⚡ Real-time Leaflet tracking is <strong style={{ color: 'var(--success)' }}>Active</strong>.<br />
            Riders' positions are mapped dynamically using real-world coordinates updated from their mobile app.
          </div>
        </div>
      </div>
    </div>
  );
}
